/**
 * "Send Form" — the manual send from the patient file (`tasks/patient-forms.md` §5.2, §13.2).
 *
 * Reached two ways, both of which end up here: the sidebar action
 * (`patient-file.component.ts` -> `navigate(undefined, 'manageForm')`) and the panel header's
 * Send button.
 *
 * ── THE 409 IS THE POINT OF THIS COMPONENT ───────────────────────────────────────────────────
 *
 * `POST /patient-form-requests` refuses a duplicate ask with 409 `OPEN_REQUEST` (§2.1, §14.3).
 * That is not an error — it is the correct answer to "send this form":
 *
 *   OPEN_REQUEST  they already have a LIVE link for it; RESEND that one, do not raise a second
 *
 * There were three. `WITHIN_REPEAT_WINDOW` and `COMPLETED_NO_REPEAT` refused because the patient
 * had completed the form before, and §30 deleted both along with the `repeatAfterDays` column.
 *
 * So the dialog STAYS OPEN and shows the sentence, with a button that takes the user to the
 * request that blocked the send. A generic red toast here would send a receptionist to the phone
 * to chase a form the patient already has in their inbox.
 *
 * The 422s (`MANUAL_SEND_DISABLED`, `FORM_NOT_PUBLISHED`, `VERSION_MISSING`) are mostly
 * unreachable because the picker is filtered to published forms with `allowManualSend` — but
 * "mostly" is doing work there: the list is a snapshot, and an admin can unpublish a form while
 * this dialog is open. They are handled the same way.
 */

import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { GroupService } from '../../../../core/services/group.service';
import {
  PatientFormRequestService,
  SendableCustomForm,
  describeSendFailure,
} from '../../../../core/services/patient-form-request.service';
import { UserService } from '../../../../core/services/user.service';

export interface PatientFormSendDialogData {
  patient: any;
}

/** What the dialog closes with. `focusRequestId` means "the user asked to see that request". */
export interface PatientFormSendDialogResult {
  sent?: boolean;
  focusRequestId?: string;
}

@Component({
  selector: 'app-patient-form-send-dialog',
  templateUrl: './patient-form-send-dialog.component.html',
  styleUrls: ['./patient-form-send-dialog.component.scss'],
})
export class PatientFormSendDialogComponent extends BaseComponent implements OnInit {

  form!: FormGroup;
  patient: any;

  /** Published, manually-sendable forms in the PATIENT'S practice. */
  availableForms: SendableCustomForm[] | null = null;
  groupUsers: any[] = [];

  sending = false;

  /** The refusal currently on screen, if any. Set by a 409/422; cleared on any edit. */
  blockedMessage: string | null = null;
  blockedRequestId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private patientFormRequestService: PatientFormRequestService,
    private groupService: GroupService,
    private userService: UserService,
    private dialogRef: MatDialogRef<PatientFormSendDialogComponent, PatientFormSendDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: PatientFormSendDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
    this.patient = data ? data.patient : null;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      customFormId: ['', Validators.required],
      physicianId: [''],
      // Email is on by default and SMS is not: email carries the form's name and description and
      // costs the practice nothing, where SMS spends a credit per send.
      email: [this.hasEmail],
      sms: [false],
    });

    // Any edit invalidates the refusal on screen — it was about a different form.
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.blockedMessage = null;
      this.blockedRequestId = null;
    });

    this.loadForms();
    this.loadPhysicians();
  }

  get hasEmail(): boolean {
    return !!(this.patient && this.patient.email);
  }

  get hasMobile(): boolean {
    return !!(this.patient && this.patient.mobileNumber);
  }

  get patientName(): string {
    if (!this.patient) return 'this patient';
    return `${this.patient.name || ''} ${this.patient.surname || ''}`.trim() || 'this patient';
  }

  get selectedForm(): SendableCustomForm | null {
    const id = this.form ? this.form.value.customFormId : null;
    if (!id || !this.availableForms) return null;
    return this.availableForms.filter((f) => f.id === id)[0] || null;
  }

  /** At least one channel, and a channel the patient can actually be reached on. */
  get canSend(): boolean {
    if (!this.form || this.form.invalid || this.sending) return false;
    const value = this.form.value;
    return (value.email && this.hasEmail) || (value.sms && this.hasMobile);
  }

  private loadForms(): void {
    const groupId = this.patient ? this.patient.groupId : undefined;
    this.patientFormRequestService.loadSendableForms(groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          const forms: SendableCustomForm[] = (result && result.data) ? result.data : [];
          // The server would answer 422 for each of these; filtering here is what makes those
          // codes "mostly unreachable" rather than the normal way this dialog fails.
          this.availableForms = forms.filter((f) =>
            f.status === 'published' && f.allowManualSend !== false && !!f.currentVersionId);
        },
        error: (error: any) => {
          console.error('Error loading sendable forms: ', error);
          this.availableForms = [];
          this.showToastMessage('The list of forms could not be loaded. Please try again.');
        },
      });
  }

  /** Same source and same filter the consent dialog uses for its physician picker. */
  private loadPhysicians(): void {
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        if (!users || !users.length) return;
        this.groupUsers = users.filter((u: any) => u.userType === 'doctor');
        const currentUser = this.userService.user;
        if (currentUser && this.groupUsers.some((gu: any) => gu.userId === currentUser.id)) {
          this.form.patchValue({ physicianId: currentUser.id }, { emitEvent: false });
        }
      });
  }

  send(): void {
    if (!this.canSend || !this.patient) return;

    const value = this.form.value;
    const channels: string[] = [];
    if (value.email && this.hasEmail) channels.push('email');
    if (value.sms && this.hasMobile) channels.push('sms');

    this.sending = true;
    this.blockedMessage = null;
    this.blockedRequestId = null;

    const formName = this.selectedForm ? this.selectedForm.name : undefined;

    this.patientFormRequestService.createRequest({
      patientId: this.patient.id,
      customFormId: value.customFormId,
      physicianId: value.physicianId || null,
      channels,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.sending = false;
          this.showToastMessage(this.describeDelivery(result, formName));
          this.dialogRef.close({ sent: true });
        },
        error: (error: any) => {
          this.sending = false;
          const failure = describeSendFailure(error, formName);
          // The dialog stays open. The user has to be able to read what happened and act on it.
          this.blockedMessage = failure.message;
          this.blockedRequestId = failure.existingRequestId || null;
        },
      });
  }

  /**
   * Say what actually went out. `deliverFormRequest` never throws and reports per channel, so a
   * form whose email bounced or whose SMS was skipped for want of credits arrives here as a 201
   * with the bad news in `delivery` — reporting a flat "Form sent" would be wrong.
   */
  private describeDelivery(result: any, formName?: string): string {
    const delivery = result ? result.delivery : null;
    const name = formName ? `"${formName}"` : 'The form';
    if (!delivery) return `${name} was sent.`;

    const problems: string[] = [];
    if (delivery.email && delivery.email !== 'sent' && delivery.email !== 'not requested') {
      problems.push(`Email ${delivery.email}`);
    }
    if (delivery.sms && delivery.sms !== 'sent' && delivery.sms !== 'not requested') {
      problems.push(`SMS ${delivery.sms}`);
    }
    if (!problems.length) return `${name} was sent to ${this.patientName}.`;
    return `${name} was created, but: ${problems.join('; ')}.`;
  }

  /**
   * "Go to the request that blocked this".
   *
   * Announced through the SERVICE rather than handed back through `afterClosed`, because this
   * dialog has two callers — the panel's own header button and the patient-file sidebar action —
   * and only one of them is the panel. Routing it through `focusRequest$` means
   * `patient-file.component.ts` needs to know nothing about form requests beyond opening this
   * dialog.
   */
  openBlockingRequest(): void {
    if (!this.blockedRequestId) return;
    this.patientFormRequestService.focusRequest(this.blockedRequestId);
    this.dialogRef.close({ focusRequestId: this.blockedRequestId });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
